import {
  AfterViewInit,
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { IdentityService } from '../core/identity/identity.service';
import { UserSession } from '../core/site-specific.base';
import { Logger, LoggerFactory } from '../core/logger.service';
import { MatSidenav } from '@angular/material/sidenav';
import { FlyoutService } from '../flyout/flyout.service';
import { ExplainerDialogService } from 'app/core/explainer-dialog.service';
import { Explainer } from 'app/shared/explainer-dialog/explainer-dialog-content';
import { SideMenuComponent } from './side-menu/side-menu.component';
import { TopLevelSideMenuService } from './top-level-side-menu/top-level-side-menu.service';
import { CurrentContextService } from 'app/core/current-context.service';
import { ExpiredAccountAccessService } from 'app/core/authorization/expired-account-access.service';
import { resolveInactiveAccountView } from './inactive-account-banner/inactive-account-view';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LayoutComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('sideMenu') private sideMenu: SideMenuComponent;
  @HostListener('click', ['$event.target']) onClick(e) {
    if (e.classList.contains('explainer')) {
      this.explainerDialogService.explainerBS.next({
        top: e.getBoundingClientRect().top + window.pageYOffset,
        left: e.getBoundingClientRect().left + window.pageXOffset,
        isExisted: true,
        menuIsOpen: this.sideMenu?.isOpen,
      });
    }
  }
  @Input() currentTitle: string;
  @Input() layoutColor: string;
  @Input() isFullscreenPage = true;
  @Input() isBetaItem = false;
  @Input() public urlPath;
  private readonly logger: Logger;
  private defaultColor = 'grey';
  public disableMenu = false;
  public isExplain = false;
  public explainerDialogServiceSub: Subscription;
  private subscriptionArray: Subscription[] = [];

  @ViewChild('sidenav', { static: true }) public sidenav: MatSidenav;

  private readonly topLevelSideMenuService = inject(TopLevelSideMenuService);
  public readonly isTopLevelMenuExpanded = this.topLevelSideMenuService.expanded;

  private readonly expiredAccountAccess = inject(ExpiredAccountAccessService);
  private readonly currentContext = toSignal(inject(CurrentContextService).currentContextBS);

  public readonly inactiveAccountView = computed(() =>
    resolveInactiveAccountView(this.currentContext()?.account, this.expiredAccountAccess.unlockedSiteIdsForAdmin()),
  );

  constructor(
    readonly userSession: UserSession,
    readonly loggerFactory: LoggerFactory,
    private readonly identityService: IdentityService,
    private flyoutService: FlyoutService,
    public explainerDialogService: ExplainerDialogService,
  ) {
    this.logger = loggerFactory.get('LayoutComponent');
  }

  protected setLayoutColor(color: string) {
    this.layoutColor = color;
  }

  ngOnInit(): void {
    this.setLayoutColor(this.defaultColor);
    this.identityService.onSignout.subscribe({
      next: () => {
        this.setLayoutColor(this.defaultColor);
        this.disableMenu = true;
      },
    });
    this.identityService.onIdentityChanged.subscribe(async () => {
      this.disableMenu = false;
    });
    this.createExplainer();
  }

  ngAfterViewInit(): void {
    this.flyoutService.setSidenav(this.sidenav);
  }

  public closeSidenav() {
    this.sidenav.close();
    this.flyoutService.close();
  }

  private createExplainer(): void {
    this.explainerDialogServiceSub = this.explainerDialogService.explainerBS.subscribe((dialog: Explainer) => {
      if (dialog) {
        this.isExplain = dialog.isExisted;
      }
    });
  }

  public ngOnDestroy(): void {
    this.subscriptionArray.push(this.explainerDialogServiceSub);
    this.subscriptionArray.forEach((subs) => subs && subs.unsubscribe());
  }
}
